/**
 * ELEGIR LA VARIACIÓN DE UN PLATO — «Hamburguesa: Pollo, Carne o Mixta».
 *
 * La misma ventana en la caja y en la comanda del mesero (2026-10-15).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ASÍ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  · UN TOQUE ELIGE. No hay «Aceptar»: quien atiende está de pie, con el
 *    cliente delante, y la variación es la única pregunta. Si el plato lleva
 *    extras, se preguntan después, en su propia ventana.
 *
 *  · CADA VARIACIÓN CON SU PRECIO COMPLETO, en grande. «Carne $6», no
 *    «+$1»: así lo dice la carta y así lo repite el cliente.
 *
 *  · Las teclas 1, 2, 3… eligen en la caja con teclado, en el orden en que se
 *    ven. El número va en la tarjeta para que se sepa cuál es cuál.
 *
 *  · Una variación agotada (le falta un ingrediente) se ve pero no se toca:
 *    desaparecer haría pensar que el plato cambió.
 */

import { useEffect } from "react";
import { cx } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { formatMoney, type FormatoMoneda } from "@/lib/money";
import type { ItemListRow, VariacionDeLista } from "@/lib/types";

export function ElegirVariacion({
  item,
  formato,
  onElegir,
  onCerrar,
}: {
  item: ItemListRow;
  formato: FormatoMoneda;
  onElegir: (variacion: VariacionDeLista) => void;
  onCerrar: () => void;
}) {
  const variaciones = item.variaciones ?? [];
  const pregunta = item.pregunta?.trim() || "Variación";


  useEffect(() => {
    function alPulsar(e: KeyboardEvent) {
      // Que escribir en un campo no elija nada.
      const destino = e.target as HTMLElement | null;
      if (destino && ["INPUT", "TEXTAREA", "SELECT"].includes(destino.tagName)) return;
      const n = Number.parseInt(e.key, 10);
      if (!Number.isInteger(n) || n < 1) return;
      const v = variaciones[n - 1];
      if (v && !v.agotada) {
        e.preventDefault();
        onElegir(v);
      }
    }
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [variaciones, onElegir]);

  return (
    <Modal
      abierto
      titulo={item.name}
      descripcion={`Selecciona ${articulo(pregunta)} ${pregunta.toLocaleLowerCase()}.`}
      onCerrar={onCerrar}
      ancho="md"
    >
      <div
        className={cx(
          "grid gap-2.5",
          variaciones.length === 2 || variaciones.length === 4
            ? "grid-cols-2"
            : "grid-cols-2 sm:grid-cols-3",
        )}
      >
        {variaciones.map((v, i) => (
          <button
            key={v.id}
            type="button"
            disabled={v.agotada}
            onClick={() => onElegir(v)}
            className={cx(
              "pulsable-suave group relative flex min-h-[92px] flex-col items-start justify-between rounded-[var(--radius-card)] border p-3.5 text-left transition-colors",
              v.agotada
                ? "cursor-default border-dashed border-line bg-transparent"
                : "border-line bg-surface hover:border-accent hover:bg-accent-soft/40 focus-visible:border-accent",
            )}
          >
            <span className="flex w-full items-start justify-between gap-2">
              <span
                className={cx(
                  "min-w-0 text-[15px] font-semibold leading-snug",
                  v.agotada ? "text-faint" : "text-ink",
                )}
              >
                {v.nombre}
              </span>
              {i < 9 && (
                <kbd
                  aria-hidden="true"
                  className="hidden shrink-0 rounded border border-line px-1.5 text-[11px] leading-5 text-faint lg:inline"
                >
                  {i + 1}
                </kbd>
              )}
            </span>
            {v.agotada ? (
              <span className="text-xs font-medium text-faint">Agotada</span>
            ) : (
              <span className="tabular text-lg font-semibold text-ink group-hover:text-accent">
                {formatMoney(precioConImpuesto(item, v.precio), formato)}
              </span>
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
}

/** «la proteína», «el tamaño»: la pregunta se lee como una frase. */
function articulo(pregunta: string): string {
  const p = pregunta.toLocaleLowerCase();
  if (/(a|ión|dad|tud|umbre)$/.test(p) && !/(ma|día)$/.test(p)) return "la";
  return "el";
}

/**
 * El precio de una variación como se ve en la tarjeta del plato: con impuesto.
 *
 * La variación viene sin él; se le aplica la misma proporción que al plato,
 * para que el número de aquí sea el mismo que después dice el carrito.
 */
export function precioConImpuesto(item: ItemListRow, precio: string): string {
  const base = Number(item.base_price);
  const conImpuesto = Number(item.price_with_tax ?? item.base_price);
  const factor = base > 0 && conImpuesto > 0 ? conImpuesto / base : 1;
  return (Number(precio) * factor).toFixed(4);
}
